import {
  IdcardOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Row,
  Col,
  Input,
  Table,
  Select,
  message,
  Spin,
} from "antd";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

const { Option } = Select;
const { RangePicker } = DatePicker;

export default function FingerHeadOffice() {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { authData, backUrl } = useContext(AuthContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = window.innerWidth < 768;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filter options from backend
  const [organisations, setOrganisations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  // Filters state
  const [filters, setFilters] = useState({
    empCode: "",
    empName: "",
    organisation: "",
    department: "",
    designation: "",
    fromDate: null,
    toDate: null,
  });

  // Debounce timer for search inputs
  const [searchTimer, setSearchTimer] = useState(null);

  // --- Fetch Organisations ---
  const fetchOrganisations = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });
      setOrganisations(res.data.results || res.data);
    } catch (err) {
      console.error("Failed to fetch organisations:", err);
      message.error("Failed to fetch organisations");
    }
  };

  // --- Fetch Departments (only after organisation selected) ---
  const fetchDepartments = async (orgId) => {
    if (!orgId) {
      setDepartments([]);
      return;
    }
    try {
      const res = await axios.get(
        `${backUrl}/api/emp/departments/?organisation=${orgId}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setDepartments(res.data.results || res.data);
    } catch (err) {
      console.error("Failed to fetch departments:", err);
      message.error("Failed to fetch departments");
      setDepartments([]);
    }
  };

  // --- Fetch Designations (only after department selected) ---
  const fetchDesignations = async (deptId) => {
    if (!deptId) {
      setDesignations([]);
      return;
    }
    try {
      const res = await axios.get(
        `${backUrl}/api/emp/designations/?department=${deptId}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setDesignations(res.data.results || res.data);
    } catch (err) {
      console.error("Failed to fetch designations:", err);
      message.error("Failed to fetch designations");
      setDesignations([]);
    }
  };

  // --- Fetch Data from API with Query Params ---
  const fetchData = async (resetPage = false) => {
    setLoading(true);
    try {
      const params = {
        page: resetPage ? 1 : currentPage,
        page_size: pageSize,
      };

      // Add filters to params
      if (filters.empCode) params.emp_code = filters.empCode;
      if (filters.empName) params.Emp_Name = filters.empName;
      if (filters.organisation) params.organisation = filters.organisation;
      if (filters.department) params.department = filters.department;
      if (filters.designation) params.designation = filters.designation;
      if (filters.fromDate) params.from_date = filters.fromDate;
      if (filters.toDate) params.to_date = filters.toDate;

      const res = await axios.get(
        `${backUrl}/api/hr/headofficefingerprint/`,
        {
          headers: {
            Authorization: `Bearer ${authData.access}`,
          },
          params,
        }
      );

      const records = res.data.results || res.data || [];
      const count = res.data.count || records.length;

      const recordsWithKeys = records.map((record, index) => ({
        ...record,
        key:
          record.id ||
          `${record.Emp_Id}-${record.Date}-${record.Time}-${index}`,
      }));

      setData(recordsWithKeys);
      setTotalRecords(count);

      if (resetPage) {
        setCurrentPage(1);
      }
    } catch (err) {
      console.error("Error fetching fingerprint data:", err);
      message.error("Failed to load fingerprint data");
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // --- Fetch organisations on mount ---
  useEffect(() => {
    fetchOrganisations();
    fetchData();
  }, [authData?.access, backUrl]);

  // --- Refetch departments when organisation changes ---
  useEffect(() => {
    if (filters.organisation) {
      fetchDepartments(filters.organisation);
    } else {
      setDepartments([]);
    }
  }, [filters.organisation]);

  // --- Refetch designations when department changes ---
  useEffect(() => {
    if (filters.department) {
      fetchDesignations(filters.department);
    } else {
      setDesignations([]);
    }
  }, [filters.department]);

  // --- Fetch data when dependencies change ---
  useEffect(() => {
    fetchData();
  }, [
    currentPage,
    pageSize,
    filters.organisation,
    filters.department,
    filters.designation,
    filters.fromDate,
    filters.toDate,
  ]);

  // --- Debounced search for text inputs ---
  useEffect(() => {
    if (searchTimer) {
      clearTimeout(searchTimer);
    }

    const timer = setTimeout(() => {
      fetchData(true); // Reset to page 1 when searching or clearing
    }, 500); // 500ms debounce

    setSearchTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [filters.empCode, filters.empName]);

  // --- Handle filter changes ---
  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };

      // Reset dependent filters
      if (key === "organisation") {
        newFilters.department = "";
        newFilters.designation = "";
      } else if (key === "department") {
        newFilters.designation = "";
      }

      return newFilters;
    });

    // Reset to first page for non-text filters
    if (key !== "empCode" && key !== "empName") {
      setCurrentPage(1);
    }
  };

  // --- Export to Excel (with current filters) ---
  const handleExportExcel = async () => {
    try {
      message.loading("Preparing export...", 0);

      // If rows are selected, export only those
      if (selectedRowKeys.length > 0) {
        const rowsToExport = data.filter((row) =>
          selectedRowKeys.includes(row.key)
        );
        exportToExcel(rowsToExport);
        return;
      }

      // Otherwise, fetch all data with current filters (no pagination)
      const params = {};
      if (filters.empCode) params.emp_code = filters.empCode;
      if (filters.empName) params.Emp_Name = filters.empName;
      if (filters.organisation) params.organisation = filters.organisation;
      if (filters.department) params.department = filters.department;
      if (filters.designation) params.designation = filters.designation;
      if (filters.fromDate) params.from_date = filters.fromDate;
      if (filters.toDate) params.to_date = filters.toDate;

      const res = await axios.get(
        `${backUrl}/api/hr/headofficefingerprint/`,
        {
          headers: {
            Authorization: `Bearer ${authData.access}`,
          },
          params,
        }
      );

      const allRecords = res.data.results || res.data || [];
      message.destroy();
      exportToExcel(allRecords);
    } catch (err) {
      message.destroy();
      console.error("Error exporting data:", err);
      message.error("Failed to export data");
    }
  };

  const exportToExcel = (records) => {
    if (!records.length) {
      message.warning("No records to export");
      return;
    }

    const formattedData = records.map((item) => ({
      "Employee Code": item.Emp_Id || "",
      "Employee Name": item.Emp_Name || "",
      Date: item.Date || "",
      Company: item?.employee_details?.organisation || "",
      Department: item?.employee_details?.department || "",
      Designation: item?.employee_details?.designation || "",
      "Device ID": item.Device_Id || "",
      "Device Name": item.Device_Name || "",
      "Punch-In Time": item.Time || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "HeadOfficeFingerprint");

    XLSX.writeFile(workbook, "HeadOffice_Fingerprint.xlsx");
    message.success(`Exported ${records.length} records successfully`);
  };

  // --- Table Columns ---
  const columns = [
    { title: "Employee Code", dataIndex: "Emp_Id", key: "Emp_Id" },
    { title: "Employee Name", dataIndex: "Emp_Name", key: "Emp_Name" },
    { title: "Date", dataIndex: "Date", key: "Date" },
    {
      title: "Company",
      dataIndex: ["employee_details", "organisation"],
      key: "Company",
    },
    {
      title: "Department",
      dataIndex: ["employee_details", "department"],
      key: "Department",
    },
    {
      title: "Designation",
      dataIndex: ["employee_details", "designation"],
      key: "Designation",
    },
    { title: "Device ID", dataIndex: "Device_Id", key: "Device_Id" },
    { title: "Device Name", dataIndex: "Device_Name", key: "Device_Name" },
    { title: "Punch-In Time", dataIndex: "Time", key: "Time" },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    },
  };

  // --- Handle pagination change ---
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    setSelectedRowKeys([]); // Clear selection on page change
  };

  return (
    <>
      <section>
        {isMobile ? (
          <center>
            <p
              style={{
                fontSize: "15px",
                textTransform: "uppercase",
                color: "#0e497b",
              }}
            >
              <IdcardOutlined /> Head Office Fingerprint Records
            </p>
          </center>
        ) : (
          <center>
            <h5>
              <IdcardOutlined /> Head Office Fingerprint Records
            </h5>
          </center>
        )}

        {/* ---------------- Filter Section ---------------- */}
        <Card
          style={{
            margin: "20px",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <label>Employee Code</label>
              <Input
                placeholder="Enter Employee Code"
                value={filters.empCode}
                onChange={(e) =>
                  handleFilterChange("empCode", e.target.value)
                }
                allowClear
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <label>Employee Name</label>
              <Input
                placeholder="Enter Employee Name"
                value={filters.empName}
                onChange={(e) =>
                  handleFilterChange("empName", e.target.value)
                }
                allowClear
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <label>Company Name</label>
              <Select
                placeholder="Select Company"
                style={{ width: "100%" }}
                allowClear
                value={filters.organisation || undefined}
                onChange={(value) =>
                  handleFilterChange("organisation", value ?? "")
                }
              >
                {organisations.map((org) => (
                  <Option key={org.id} value={org.id}>
                    {org.name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <label>Department</label>
              <Select
                showSearch
                placeholder="Select Department"
                style={{ width: "100%" }}
                allowClear
                value={filters.department || undefined}
                onChange={(value) =>
                  handleFilterChange("department", value ?? "")
                }
                filterOption={(input, option) =>
                  (option?.children ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                disabled={!filters.organisation}
              >
                {departments.map((dept) => (
                  <Option key={dept.id} value={dept.id}>
                    {dept.name}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: "10px" }}>
            <Col xs={24} sm={12} md={9} lg={6}>
              <label>Designation</label>
              <Select
                showSearch
                placeholder="Select Designation"
                style={{ width: "100%" }}
                allowClear
                value={filters.designation || undefined}
                onChange={(value) =>
                  handleFilterChange("designation", value ?? "")
                }
                filterOption={(input, option) =>
                  (option?.children ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                disabled={!filters.department}
              >
                {designations.map((des) => (
                  <Option key={des.id} value={des.id}>
                    {des.name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <label>Date Range</label>
              <RangePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                value={
                  filters.fromDate && filters.toDate
                    ? [dayjs(filters.fromDate, "YYYY-MM-DD"), dayjs(filters.toDate, "YYYY-MM-DD")]
                    : null
                }
                onChange={(dates, dateStrings) => {
                  if (dates && dates.length === 2) {
                    // Convert to YYYY-MM-DD format for backend
                    const fromDate = dates[0].format("YYYY-MM-DD");
                    const toDate = dates[1].format("YYYY-MM-DD");
                    setFilters({
                      ...filters,
                      fromDate,
                      toDate,
                    });
                    setCurrentPage(1);
                  } else {
                    setFilters({
                      ...filters,
                      fromDate: null,
                      toDate: null,
                    });
                    setCurrentPage(1);
                  }
                }}
              />
            </Col>
          </Row>
        </Card>

        {/* ---------------- Actions & Table ---------------- */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "15px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <Button
            icon={<DownloadOutlined />}
            style={{
              height: "36px",
              width: "180px",
              backgroundColor: "#0e497b",
              color: "white",
            }}
            onClick={handleExportExcel}
            disabled={loading}
          >
            Download{" "}
            {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : "All"}
          </Button>

          {selectedRowKeys.length > 0 && (
            <Button onClick={() => setSelectedRowKeys([])}>
              Clear Selection
            </Button>
          )}

          {/* <div style={{ color: "#666", fontSize: "14px" }}>
            Total Records: {totalRecords}
          </div> */}
        </div>

        <Spin spinning={loading} tip="Loading fingerprint records...">
          <Table
            style={{ marginTop: "10px" }}
            columns={columns}
            dataSource={data}
            bordered
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalRecords,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} records`,
              pageSizeOptions: ["10", "20", "50", "100", "200"],
            }}
            onChange={handleTableChange}
            scroll={{ x: "max-content" }}
            rowSelection={rowSelection}
            rowKey="key"
            components={{
              header: {
                cell: (props) => (
                  <th
                    {...props}
                    style={{
                      backgroundColor: "#0e497b",
                      color: "white",
                    }}
                  >
                    {props.children}
                  </th>
                ),
              },
            }}
          />
        </Spin>
      </section>
    </>
  );
}